import { useState, useEffect } from 'react';
import { Link2, Check, X, RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import axios from 'axios';

interface ConnectionStatus {
  connected: boolean;
  realmId?: string;
  lastSyncAt?: string;
  companyName?: string;
}

const Integrations = () => {
  const [qbStatus, setQbStatus] = useState<ConnectionStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check URL parameters for OAuth callback success/error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const successParam = params.get('success');
    const errorParam = params.get('error');
    const source = params.get('source');

    if (successParam === 'true' && source === 'quickbooks') {
      setSuccess('QuickBooks connected successfully!');
      // Clear URL parameters
      window.history.replaceState({}, '', '/settings/integrations');
      // Refresh status
      fetchStatus();
    } else if (errorParam) {
      setError(`Connection failed: ${errorParam}`);
      window.history.replaceState({}, '', '/settings/integrations');
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/quickbooks/status');
      setQbStatus(response.data);
    } catch (err) {
      console.error('Error fetching QuickBooks status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/v1/quickbooks/connect');
      const { authUrl } = response.data;

      // Redirect to QuickBooks authorization page
      window.location.href = authUrl;
    } catch (err) {
      setError('Failed to initiate QuickBooks connection');
      console.error('Error connecting to QuickBooks:', err);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect QuickBooks? This will stop automatic syncing.')) {
      return;
    }

    try {
      setError(null);
      await axios.post('/api/v1/quickbooks/disconnect');
      setSuccess('QuickBooks disconnected successfully');
      fetchStatus();
    } catch (err) {
      setError('Failed to disconnect QuickBooks');
      console.error('Error disconnecting QuickBooks:', err);
    }
  };

  const handleSync = async () => {
    try {
      setError(null);
      setSuccess(null);
      setSyncing(true);

      const response = await axios.post('/api/v1/quickbooks/sync/accounts');
      setSuccess(response.data.message);
      fetchStatus();
    } catch (err) {
      setError('Failed to sync accounts from QuickBooks');
      console.error('Error syncing accounts:', err);
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Integrations</h1>
        <p className="text-neutral-600">
          Connect your accounting software to automatically sync data
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-success-50 border-l-4 border-success-500 p-4 rounded">
          <div className="flex items-center">
            <Check className="w-5 h-5 text-success-600 mr-2" />
            <p className="text-sm text-success-800">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-alert-50 border-l-4 border-alert-500 p-4 rounded">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-alert-600 mr-2" />
            <p className="text-sm text-alert-800">{error}</p>
          </div>
        </div>
      )}

      {/* QuickBooks Integration Card */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            {/* QuickBooks Logo */}
            <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">QB</span>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-neutral-900">QuickBooks Online</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Sync your chart of accounts and transactions automatically
              </p>

              {/* Connection Status */}
              <div className="flex items-center mt-2">
                {loading ? (
                  <span className="text-sm text-neutral-500">Checking connection...</span>
                ) : qbStatus.connected ? (
                  <>
                    <div className="w-2 h-2 bg-success-500 rounded-full mr-2"></div>
                    <span className="text-sm font-medium text-success-600">Connected</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-neutral-400 rounded-full mr-2"></div>
                    <span className="text-sm font-medium text-neutral-600">Not Connected</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Connect/Disconnect Button */}
          <div>
            {qbStatus.connected ? (
              <button
                onClick={handleDisconnect}
                className="px-4 py-2 border border-alert-500 text-alert-600 rounded-lg hover:bg-alert-50 transition flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Disconnect</span>
              </button>
            ) : (
              <button
                onClick={handleConnect}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
              >
                <Link2 className="w-4 h-4" />
                <span>Connect QuickBooks</span>
              </button>
            )}
          </div>
        </div>

        {/* Connection Details */}
        {qbStatus.connected && (
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Connection Details</h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-neutral-600">Company</p>
                <p className="text-sm font-medium text-neutral-900">
                  {qbStatus.companyName || 'QuickBooks Company'}
                </p>
              </div>

              <div>
                <p className="text-xs text-neutral-600">Realm ID</p>
                <p className="text-sm font-mono text-neutral-900">
                  {qbStatus.realmId?.substring(0, 16)}...
                </p>
              </div>

              <div>
                <p className="text-xs text-neutral-600">Last Synced</p>
                <p className="text-sm text-neutral-900">{formatDate(qbStatus.lastSyncAt)}</p>
              </div>

              <div>
                <p className="text-xs text-neutral-600">Status</p>
                <p className="text-sm text-success-600 font-medium">Active</p>
              </div>
            </div>

            {/* Sync Actions */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-100">
              <div>
                <h4 className="text-sm font-semibold text-neutral-900">Data Synchronization</h4>
                <p className="text-xs text-neutral-600 mt-1">
                  Sync your chart of accounts from QuickBooks
                </p>
              </div>

              <button
                onClick={handleSync}
                disabled={syncing}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                <span>{syncing ? 'Syncing...' : 'Sync Now'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Features List */}
        {!qbStatus.connected && (
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">What you'll get:</h3>

            <ul className="space-y-2">
              {[
                'Automatic chart of accounts sync',
                'Real-time transaction updates',
                'Balance reconciliation',
                'Expense categorization',
                'Revenue tracking',
                'Financial statement generation',
              ].map((feature, index) => (
                <li key={index} className="flex items-center text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-success-600 mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> You'll be redirected to QuickBooks to authorize access. Your
                credentials are never stored on our servers.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Coming Soon Integrations */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Coming Soon</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: 'Xero', color: 'bg-blue-600' },
            { name: 'NetSuite', color: 'bg-orange-600' },
            { name: 'Sage', color: 'bg-green-700' },
          ].map((integration) => (
            <div
              key={integration.name}
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 opacity-60"
            >
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 ${integration.color} rounded-lg`}></div>
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">{integration.name}</h3>
                  <p className="text-xs text-neutral-500">Coming soon</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-neutral-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Need Help?</h3>
        <p className="text-sm text-neutral-600 mb-4">
          Check out our integration guides or contact support if you need assistance setting up
          QuickBooks.
        </p>
        <div className="flex items-center space-x-4">
          <a
            href="#"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
          >
            View Documentation
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a
            href="#"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
          >
            Contact Support
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Integrations;
